import type { ReactNode } from 'react';
import { curso } from '@curso/content/curso';
import { Marca } from '@shared/components/Marca';
import { ProvedorDeContato, BotaoDeContato, useContato } from '@inscricao/components/Contato';
import { RASCUNHO } from '@shared/lib/modo';
import { irParaAba } from '@shared/hooks/useAbaAtiva';
import { ABAS } from '@curso/abas';

/**
 * Cabeçalho enxuto de propósito.
 *
 * A navegação mora na tira de abas, e não aqui. Ter os dois era o mesmo menu
 * duas vezes, uma embaixo da outra, e ninguém entendia qual mandava. Aqui
 * ficam só a marca e a única ação da página.
 */
function Cabecalho() {
  return (
    <header className="sticky top-0 z-30 border-b border-linha bg-papel/90 backdrop-blur-md">
      <div className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-4 px-6 sm:px-10">
        {/*
          O simbolo entra aqui, ao lado da palavra, e nao no lugar dela: a
          marca ainda e provisoria e a rosa dos ventos sozinha nao identifica
          nada para quem chega. Com as letras, como na imagem de 20/10: a 40px
          elas ainda se leem.
        */}
        <a
          href="#abertura"
          className="flex items-center gap-2.5 text-tinta"
          aria-label={`${curso.marca}, ir para o topo`}
        >
          <Marca tamanho={40} className="shrink-0" />
          <span className="font-display text-[1.6rem] leading-none font-semibold tracking-tight">
            {curso.marca}
          </span>
        </a>

        {/*
          Ate 19/10 este botao era uma ancora que so rolava a pagina ate o
          bloco final. Agora ele abre o dialogo e faz o que promete.

          O rotulo e diferente dos outros de proposito: ele acompanha a
          rolagem e chega sozinho em qualquer aba, sem nenhuma frase por perto
          explicando que lista e essa, entao precisa dizer o resultado.
        */}
        <BotaoDeContato origem="cabecalho">Avise quando abrir</BotaoDeContato>
      </div>
    </header>
  );
}

function Rodape() {
  const abrirContato = useContato();

  return (
    <footer className="bg-papel-fundo">
      <div className="mx-auto w-full max-w-5xl px-6 py-12 sm:px-10">
        <div className="grade-bloco">
          <div className="margem-bloco">
            <span className="rotulo">Rodapé</span>
          </div>
          <div className="max-w-2xl">
            <p className="font-display text-2xl leading-none font-medium">
              {curso.marca}
            </p>
            <p className="mt-3 text-apoio text-tinta-suave">
              {curso.data}. Comercialização e estrutura por {curso.parceiro}.
            </p>

            <nav
              aria-label="Rodapé"
              className="mt-7 flex flex-col border-t border-linha pt-2 text-apoio text-tinta-suave sm:flex-row sm:flex-wrap sm:gap-x-6 sm:pt-5"
            >
              {ABAS.map((aba) => (
                <a
                  key={aba.id}
                  href={`#${aba.id}`}
                  onClick={(evento) => {
                    evento.preventDefault();
                    irParaAba(aba.id);
                    document.getElementById(`aba-${aba.id}`)?.scrollIntoView({ block: 'center' });
                  }}
                  className="link-rodape flex min-h-11 items-center sm:min-h-0"
                >
                  <span>{aba.rotulo}</span>
                </a>
              ))}
              <button
                type="button"
                onClick={() => abrirContato('lista', 'rodape')}
                className="link-rodape flex min-h-11 items-center text-left sm:min-h-0"
              >
                <span>Quero ser avisado</span>
              </button>
            </nav>

            <p className="mt-6 flex flex-wrap gap-x-5 gap-y-1 text-miudo text-tinta-fraca">
              <a href="/privacidade" className="link-rodape">
                <span>Aviso de privacidade</span>
              </a>
              <a href="/sair" className="link-rodape">
                <span>Sair da lista</span>
              </a>
            </p>

            {RASCUNHO ? (
              <p className="mt-8 border-l-2 border-sinal pl-4 text-miudo leading-relaxed text-tinta-fraca">
                Rascunho de estrutura. Nome, tipografia, cor e endereços ainda não foram
                decididos. O que está marcado em âmbar é buraco conhecido, não descuido.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <ProvedorDeContato>
      <a
        href="#abertura"
        className="sr-only focus:not-sr-only focus:absolute focus:z-40 focus:m-3 focus:bg-tinta focus:px-4 focus:py-2 focus:text-papel"
      >
        Pular para o conteúdo
      </a>
      <Cabecalho />
      <main>{children}</main>
      <Rodape />
    </ProvedorDeContato>
  );
}
